// fleet-generated: do not edit by hand
//
// Copied verbatim  : infra/fleet/artifacts/version/client-update.ts
// Parameters       : none — below this header, identical across the fleet
// Destination      : packages/fleet/version/src/client-update.ts
//
// TO CHANGE THIS FILE, edit the canonical copy in infra and re-render. It
// changes for the whole fleet at once. Editing it here is reverted by the
// next render, and the guard in this repo goes red in the meantime:
//
//     cd ../infra && node fleet/fleet.ts render --repo folio-office-app
//     node fleet/fleet.ts check --repo folio-office-app
//
// The generator runs LOCALLY and is never invoked by CI. CI only verifies
// that what is committed matches the hash in .github/fleet-lock.json, which
// also records WHICH infra commit produced it. The commit is deliberately
// not repeated here: one value, one place — and stamping it into every file
// meant a one-line template edit rewrote four files in 28 repos.
import { createSignal } from "solid-js";

/**
 * Platform side of the silent self-update: how a web build actually replaces
 * itself once `packages/shared/src/version/self-update.ts` decides it must.
 *
 * This deliberately does NOT re-implement the PWA update chain. `main.tsx`
 * already registers the Service Worker and wires
 * `updatefound` -> `SKIP_WAITING` -> `controllerchange` -> one reload
 * (validated A→B on all 12 products, see `infra/PWA_UPDATES.md`). The only
 * thing missing for a fully automatic update was the *trigger*: nothing ever
 * called `registration.update()`, so a tab that stayed open never noticed a new
 * build. That is precisely the gap here — we call it, and the existing chain
 * does the rest.
 *
 * The plain `location.reload()` is a FALLBACK, not the mechanism: it covers
 * Tauri (no Service Worker at all) and the case where `update()` finds nothing
 * to install. On its own a reload cannot defeat a stale precache, which is why
 * the caller's attempt budget exists.
 */

const SW_UPDATE_FALLBACK_MS = 12_000;

/**
 * Visible only on the degraded path: the self-update budget ran out (or could
 * not be counted) and the app is still too old for the API. `null` means "all
 * good", which is the case the user must never be told about.
 */
const [updateStalled, setUpdateStalled] = createSignal(false);

export { updateStalled };

export function reportUpdateStalled(): void {
  setUpdateStalled(true);
}

/** Full-weight reload that also drops the HTTP cache entry for the document. */
export function forceReload(): void {
  globalThis.location.reload();
}

/**
 * Asks the platform to fetch and activate a newer build. Returns immediately;
 * the reload happens through the Service Worker chain or the fallback timer.
 */
export function requestClientUpdate(): void {
  if (!("serviceWorker" in navigator)) {
    // Tauri and any SW-less environment: a reload is all we have.
    forceReload();
    return;
  }

  // Armed unconditionally, including on success: `update()` resolving only
  // means the CHECK finished, not that a new worker took control. If the
  // existing `controllerchange` chain reloads first, this timer never fires.
  setTimeout(forceReload, SW_UPDATE_FALLBACK_MS);

  void refreshServiceWorker();
}

async function refreshServiceWorker(): Promise<void> {
  try {
    const registration = await navigator.serviceWorker.getRegistration();
    if (!registration) {
      return;
    }
    // If a new worker is ALREADY waiting (installed on a previous visit but
    // never activated), poke it directly — `update()` would find nothing new to
    // install and we would sit on the fallback timer for no reason.
    if (registration.waiting) {
      // A ServiceWorker's `postMessage(message, transfer?)` takes no
      // targetOrigin — that second argument is the transfer list. The rule is
      // written for Window.postMessage and is a false positive here.
      // oxlint-disable-next-line unicorn/require-post-message-target-origin
      registration.waiting.postMessage({ type: "SKIP_WAITING" });
      return;
    }
    await registration.update();
  } catch (err) {
    console.warn("[client-update] service worker update failed:", err);
  }
}
